'use client';

import Link from 'next/link';
import { ArrowLeft } from 'lucide-react';

export default function APIReferencePage() {
  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 dark:from-slate-950 dark:via-blue-950/30 dark:to-indigo-950/20">
      {/* Animated background orbs */}
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 dark:opacity-10 animate-blob" />
      <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 dark:opacity-10 animate-blob animation-delay-2000" />

      <div className="relative container mx-auto px-4 py-16 pt-32 max-w-5xl">
        {/* Back button */}
        <Link
          href="/en/resources/documentation"
          className="group inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/60 dark:bg-slate-900/60 backdrop-blur-xl border border-white/20 dark:border-slate-700/50 text-slate-700 dark:text-slate-300  transition-all mb-12"
        >
          <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
          <span className="font-semibold">Back to Documentation</span>
        </Link>
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-7xl font-black bg-gradient-to-r from-slate-900 via-blue-900 to-indigo-900 dark:from-white dark:via-blue-100 dark:to-indigo-100 bg-clip-text text-transparent leading-tight mb-6">
            API Reference
          </h1>
          <p className="text-2xl text-slate-600 dark:text-slate-400 leading-relaxed max-w-3xl mx-auto">
            Complete REST API documentation for automating backup operations and integrating with your infrastructure.
          </p>
        </div>

        {/* Content */}
        <div className="relative">
          <div className="absolute -inset-4 bg-white/40 dark:bg-slate-900/40 backdrop-blur-3xl rounded-2xl -z-10" />
          <div className="prose prose-lg prose-slate dark:prose-invert max-w-none">

        <h2>Authentication</h2>
        <p>All API requests require authentication using an API key in the Authorization header:</p>
        <pre><code>Authorization: Bearer YOUR_API_KEY</code></pre>
        <p>Generate API keys in <strong>Settings</strong> → <strong>API Keys</strong></p>

        <h2>Base URL</h2>
        <pre><code>https://api.nexus.next-pal.com/v1</code></pre>

        <h2>Servers</h2>
        
        <h3>List Servers</h3>
        <pre><code>GET /servers</code></pre>
        <p><strong>Response:</strong></p>
        <pre><code>{`{
  "success": true,
  "data": {
    "servers": [
      {
        "id": 1,
        "name": "Production DB",
        "hostname": "db.example.com",
        "status": "active"
      }
    ]
  }
}`}</code></pre>

        <h3>Create Server</h3>
        <pre><code>POST /servers</code></pre>
        <p><strong>Request Body:</strong></p>
        <pre><code>{`{
  "name": "Production DB",
  "hostname": "db.example.com",
  "port": 22,
  "username": "backup",
  "sshKey": "ssh-rsa AAAA..."
}`}</code></pre>

        <h2>Backups</h2>

        <h3>List Backups</h3>
        <pre><code>GET /backups?serverId=1</code></pre>
        <p><strong>Query Parameters:</strong></p>
        <ul>
          <li><code>serverId</code> - Filter by server ID</li>
          <li><code>type</code> - Filter by type (full, diff, incr)</li>
          <li><code>limit</code> - Number of results (default: 50)</li>
        </ul>

        <h3>Create Backup</h3>
        <pre><code>POST /backups</code></pre>
        <p><strong>Request Body:</strong></p>
        <pre><code>{`{
  "serverId": 1,
  "type": "full",
  "stanza": "main"
}`}</code></pre>

        <h3>Get Backup Status</h3>
        <pre><code>GET /backups/:id</code></pre>

        <h2>Restore Operations</h2>

        <h3>Restore from Backup</h3>
        <pre><code>POST /restore</code></pre>
        <p><strong>Request Body:</strong></p>
        <pre><code>{`{
  "backupId": 123,
  "targetTime": "2024-01-15T10:30:00Z",
  "targetPath": "/var/lib/postgresql/restore"
}`}</code></pre>

        <h2>Schedules</h2>

        <h3>Create Schedule</h3>
        <pre><code>POST /schedules</code></pre>
        <p><strong>Request Body:</strong></p>
        <pre><code>{`{
  "serverId": 1,
  "type": "full",
  "cron": "0 2 * * 0",
  "enabled": true
}`}</code></pre>

        <h2>Webhooks</h2>
        <p>Configure webhooks to receive real-time notifications:</p>
        <pre><code>POST /webhooks</code></pre>
        <p><strong>Request Body:</strong></p>
        <pre><code>{`{
  "url": "https://your-app.com/webhook",
  "events": ["backup.completed", "backup.failed"],
  "secret": "your-webhook-secret"
}`}</code></pre>

        <h2>Rate Limits</h2>
        <ul>
          <li><strong>Free tier:</strong> 100 requests/hour</li>
          <li><strong>Professional:</strong> 1,000 requests/hour</li>
          <li><strong>Enterprise:</strong> Unlimited</li>
        </ul>

        <h2>Error Codes</h2>
        <table>
          <thead>
            <tr>
              <th>Code</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>400</td>
              <td>Bad Request - Invalid parameters</td>
            </tr>
            <tr>
              <td>401</td>
              <td>Unauthorized - Invalid API key</td>
            </tr>
            <tr>
              <td>403</td>
              <td>Forbidden - Insufficient permissions</td>
            </tr>
            <tr>
              <td>404</td>
              <td>Not Found - Resource doesn't exist</td>
            </tr>
            <tr>
              <td>429</td>
              <td>Too Many Requests - Rate limit exceeded</td>
            </tr>
            <tr>
              <td>500</td>
              <td>Internal Server Error</td>
            </tr>
          </tbody>
        </table>

        <div className="bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg p-6 mt-8">
          <h3 className="text-lg font-bold mb-2">SDK Libraries</h3>
          <p className="mb-2">Official SDKs available for:</p>
          <ul className="mb-0">
            <li>Python - <code>pip install nextpal-nexus</code></li>
            <li>Node.js - <code>npm install @nextpal/nexus</code></li>
            <li>Go - <code>go get github.com/nextpal/nexus-go</code></li>
          </ul>
        </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
      `}</style>
    </div>
  );
}
